/**
 * SaleDocumentsPage — VEN-03/04/05: Documentos de Venta
 * Lista, crea, emite y anula documentos de venta.
 */
import { todayISO } from '../../../lib/utils'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Plus, Loader2, FileText, Send, XCircle, FileMinus2, Link, Copy, Files, CheckCircle2, RefreshCw, Download, Mail,
  X, UserSquare2, Trash2, SlidersHorizontal,
} from 'lucide-react'
import { useFilters, type FilterConfig, type FilterOption } from '@/components/filters'
import { DocumentsFilterToolbar } from './DocumentsFilterToolbar'
import { PaymentLinkModal } from './PaymentLinkModal'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import {
  type SortState,
  sortStateToParam,
  loadSortFromStorage,
  saveSortToStorage,
} from '@/components/SortableHeader'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { ordenControlado } from '@/components/ds/ordenControlado'
import { useDTETypes } from '@/modules/config/hooks/useConfig'
import { KebabMenu } from '@/components/KebabMenu'
import { ToastContainer, useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import {
  useSaleDocuments,
  useSaleDocument,
  useCreateDocument,
  useCloneDocument,
  useUpdateDocument,
  useEmitDocument,
  useCancelDocument,
  useDeleteDraftDocument,
  useAdjustDocument,
  useRetryDocumentSend,
  useSaveDocumentAsTemplate,
  useDocumentVoidStatus,
  useVoidDocument,
  useCustomers,
  type SaleDocumentListItem,
  type SaleDocument,
} from '@/modules/sales/hooks/useSales'
import { useLocalizationConfig, useGlobalCustomFields, useCurrencyRates, type GlobalCustomField } from '@/modules/config/hooks/useConfig'
import DatePicker from '@/components/DatePicker'
import { useCreatePaymentLink, usePaymentGateways } from '@/modules/payments/hooks/usePayments'
import apiClient from '@/lib/apiClient'
import { PageHeader } from '@/components/HelpPanel'
import { PageSizeSelector, DEFAULT_PAGE_SIZE } from '@/components/PageSizeSelector'
import { useSellers } from '@/modules/users/hooks/useUsers'
import { HELP } from '@/data/helpContent'
import { DocumentModal } from './DocumentModal'
import { DocumentDetailModal } from './DocumentDetailModal'
import { SubscriptionModal } from './SubscriptionModal'
import { SendEmailModal } from './SendEmailModal'
import { fmtDate, formatRut } from '@/lib/utils'
import { formatMoneyForTenant, useMoney, useTenantLocale } from '@/lib/locale'
import {
  COLLECTION_STATUS,
  DTE_STATUS,
  StatusBadge as DsStatusBadge,
} from '@/components/ds/StatusBadge'
import { usePermission } from '@/hooks/usePermission'
import { paisUsaComunicacionDeBaja } from '@/lib/documentNumbering'
import { Pagination } from '@/components/ds/Pagination'
import { Modal } from '@/components/ds/Modal'
import { useSiiNotice } from '@/modules/platform/hooks/usePlatform'

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Extrae el primer string de un `error.details` anidado (ej. lines[i].unit_price[0]). */
function firstErrorString(v: unknown): string | null {
  if (typeof v === 'string') return v
  if (Array.isArray(v)) {
    for (const x of v) { const s = firstErrorString(x); if (s) return s }
    return null
  }
  if (v && typeof v === 'object') {
    for (const x of Object.values(v)) { const s = firstErrorString(x); if (s) return s }
    return null
  }
  return null
}


/**
 * El estado del documento sale del catálogo del design system.
 *
 * Antes vivía acá, en un `STATUS_CONFIG` con diez entradas, y cuatro de ellas
 * (`xml_signed`, `pending_send`, `querying_status` y los puntos de otras dos)
 * usaban paletas crudas —indigo, yellow, cyan, violet— que no siguen el tema ni
 * el color del tenant. El catálogo las reduce a cinco tonos semánticos.
 *
 * Los rótulos NO cambian: son los mismos que la tabla mostraba.
 */
function StatusBadge({ status }: { status: SaleDocumentListItem['status'] }) {
  // Con el aviso de demora del SII encendido, «Consultando» explica por qué
  // tarda: es lo que el cliente mira cuando duda si volver a emitir.
  const { data } = useSiiNotice()
  const esperaAlSii = status === 'querying_status' || status === 'sent_facturador'
  const titulo = data?.notice && esperaAlSii
    ? 'El SII está demorando la validación. Se actualiza solo; no es necesario volver a emitir.'
    : undefined
  return <DsStatusBadge status={status} catalog={DTE_STATUS} title={titulo} />
}

type AdjustmentNonNormal = Exclude<NonNullable<SaleDocumentListItem['adjustment_status']>, 'normal'>

/** El tono de cada ajuste. El rótulo lo decide `AdjustmentBadge`, que depende
    del tipo de documento. */
const TONO_AJUSTE: Record<AdjustmentNonNormal, 'warning' | 'danger'> = {
  adjusted: 'warning',
  annulled: 'danger',
  annulled_after_payment: 'danger',
}

const ROTULO_AJUSTE: Record<AdjustmentNonNormal, string> = {
  adjusted: 'Ajustado',
  annulled: 'Anulado por NC',
  annulled_after_payment: 'Anulado (crédito)',
}

function AdjustmentBadge({ status, docType }: {
  status?: SaleDocumentListItem['adjustment_status']
  docType?: string
}) {
  if (!status || status === 'normal') return null
  // Una NC/ND solo se «anula» vía otra NC/ND con cod_ref=1. El rótulo «Anulado
  // por NC» suena raro en una NC — se invierte según el tipo, y el default
  // queda para FE/FNA y el resto. Esta regla es de negocio: no se delega.
  let label = ROTULO_AJUSTE[status]
  if (status === 'annulled') {
    if (docType === 'NC' || docType === 'NCX') label = 'Anulado por ND'
    else if (docType === 'ND' || docType === 'NDX') label = 'Anulado por NC'
  }
  return <DsStatusBadge label={label} tone={TONO_AJUSTE[status]} title={label} />
}

function CollectionBadge({ status, display, adjustmentStatus }: {
  status?: SaleDocumentListItem['collection_status'];
  display?: string;
  adjustmentStatus?: SaleDocumentListItem['adjustment_status'];
}) {
  // Una factura anulada por NC cae en collection_status='paid' (adjusted_total
  // <= 0), lo que se leía como «Pagada» — engañoso, porque no se cobró: se
  // anuló. El estado parcial ('adjusted') ya queda bien porque
  // outstanding_amount netea la NC. Es regla de negocio, no de presentación.
  if (adjustmentStatus === 'annulled' || adjustmentStatus === 'annulled_after_payment') {
    return <DsStatusBadge label="Anulada" tone="neutral" title="Saldo anulado por Nota de Crédito" />
  }
  if (!status) return <span className="text-xs text-content-subtle">—</span>
  // `display` es el rótulo que manda el backend, y cubre un estado que el
  // catálogo todavía no tenga.
  return <DsStatusBadge status={status} catalog={COLLECTION_STATUS} label={COLLECTION_STATUS[status] ? undefined : (display || status)} />
}

function DocTypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    FE: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
    FNA: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300',
    BE: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    BNA: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300',
    LF: 'bg-warning-surface text-warning-content',
    FC: 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
    GD: 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
    NC: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    ND: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',
    FEX: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300',
    NDX: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300',
    NCX: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300',
  }
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold font-mono ${colors[type] || 'bg-surface-muted text-content-muted'}`}>
      {type}
    </span>
  )
}

function CancelModal({ docName, onConfirm, onCancel, isPending }: {
  docName: string
  onConfirm: (reason: string) => void
  onCancel: () => void
  isPending: boolean
}) {
  const [reason, setReason] = useState('')
  return (
    <Modal
      open
      onClose={onCancel}
      size="sm"
      title="Anular documento"
      subtitle={<>Vas a anular: <strong>{docName}</strong></>}
      actions={
        <>
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft transition-colors">
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-danger-solid text-white text-sm rounded-lg hover:opacity-90 disabled:opacity-60 transition-colors"
          >
            {isPending && <Loader2 size={13} className="animate-spin" />}
            Anular
          </button>
        </>
      }
    >
      <textarea
        aria-label="Motivo de anulación"
        value={reason}
        onChange={e => setReason(e.target.value)}
        placeholder="Motivo de anulación (opcional)"
        rows={3}
        className="w-full px-3 py-2 text-sm border border-divider rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
      />
    </Modal>
  )
}

/**
 * Comunicacion de baja (Peru) — pedirle a SUNAT que deje SIN EFECTO un
 * comprobante que ya acepto.
 *
 * Es un modal aparte del de anular y no una variante: son tramites distintos.
 * Anular es interno y solo aplica a borradores y rechazados; esto sale hacia
 * SUNAT, tiene plazo y puede ser rechazado. El motivo aca es OBLIGATORIO porque
 * viaja en la comunicacion.
 *
 * El estado se consulta al abrir (`useDocumentVoidStatus`) y no en el listado:
 * seria un request por fila para una accion que casi nunca se usa.
 */
function VoidModal({ doc, onClose }: { doc: SaleDocumentListItem; onClose: () => void }) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const { data: estado, isLoading } = useDocumentVoidStatus(doc.id)
  const voidDoc = useVoidDocument()
  const numero = doc.folio || doc.id.slice(0, 8)
  // Una boleta NO se anula con una comunicación de baja: se informa en el
  // resumen diario. Es otro trámite y la ventana lo dice, para que el usuario
  // sepa qué va a pasar y por qué el plazo cuenta desde otra fecha.
  const porResumen = estado?.mechanism === 'daily_summary'
  const titulo = porResumen ? 'Anular boleta ante SUNAT' : 'Comunicar baja a SUNAT'

  const confirmar = () => {
    if (!reason.trim()) return setError('El motivo es obligatorio: SUNAT lo exige.')
    setError('')
    voidDoc.mutate({ id: doc.id, reason }, {
      onSuccess: onClose,
      onError: (err: any) =>
        setError(err?.response?.data?.error?.message || 'No se pudo comunicar la baja.'),
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={titulo}
      subtitle={<>Comprobante <strong>{numero}</strong></>}
      actions={
        <>
          <button onClick={onClose} className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft transition-colors">
            Cerrar
          </button>
          {estado?.can_void && (
            <button
              onClick={confirmar}
              disabled={voidDoc.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 bg-danger-solid text-white text-sm rounded-lg hover:opacity-90 disabled:opacity-60 transition-colors"
            >
              {voidDoc.isPending && <Loader2 size={13} className="animate-spin" />}
              {porResumen ? 'Anular boleta' : 'Comunicar baja'}
            </button>
          )}
        </>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 size={20} className="animate-spin text-content-subtle" />
        </div>
      ) : estado?.can_void ? (
        <>
          <div className="mb-4 p-3 rounded-lg bg-warning-surface border border-warning-border">
            <p className="text-xs text-warning-content-strong">
              El comprobante queda <strong>sin efecto</strong>, como si no se hubiera
              emitido.{' '}
              {porResumen && (
                <>La anulación se informa en el <strong>resumen diario</strong> que
                envía tu facturador electrónico, y el plazo cuenta desde la fecha
                de emisión.{' '}</>
              )}
              {typeof estado.days_left === 'number' && (
                <>Quedan <strong>{estado.days_left} día{estado.days_left === 1 ? '' : 's'}</strong> de
                plazo{estado.deadline ? ` (hasta el ${estado.deadline})` : ''}.</>
              )}
            </p>
          </div>
          <label htmlFor="saledocumentspage-motivo-de-la-baja" className="block text-xs font-medium text-content mb-1">
            Motivo de la baja <span className="text-danger-content-soft">*</span>
          </label>
          <textarea id="saledocumentspage-motivo-de-la-baja"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Por ejemplo: error en el monto, la operación no se realizó"
            rows={3}
            className="w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface text-content focus:outline-none focus:ring-2 focus:ring-focus-ring mb-2"
          />
        </>
      ) : (
        <div className="mb-4 p-3 rounded-lg bg-soft border border-divider">
          <p className="text-xs text-content">{estado?.reason_blocked}</p>
        </div>
      )}

      {estado?.last_request && (
        <p className="text-[11px] text-content-muted mb-2">
          Último intento: {estado.last_request.status_label}
          {estado.last_request.response_note ? ` — ${estado.last_request.response_note}` : ''}
        </p>
      )}
      {error && <p className="text-xs text-danger-content-soft mb-2">{error}</p>}
    </Modal>
  )
}

// ─── DocumentRow ──────────────────────────────────────────────────────────────

/**
 * Las columnas del listado de documentos.
 *
 * 🔴 Esto era `DocumentRow`, un componente que emitía su propio `<tr>` — el
 * bloqueo «componente-fila» del inventario del barrido. Resultó **aparente**:
 * el componente era render puro (sin estado ni consultas propias), así que cada
 * `<td>` se convierte en el `render` de una columna sin perder nada, y la tabla
 * gana lo que el componente del sistema fija: filas alternas, orden por clic con
 * `aria-sort` y `.cl-num` en los montos.
 *
 * ⚠️ El impedimento de verdad es un componente de fila que **consulte o escriba
 * lo suyo** —como `PeriodInputRow` en nómina—, no el mero hecho de estar
 * envuelto en un componente.
 *
 * Los formateadores llegan por parámetro y no se resuelven acá con hooks: esta
 * función se llama dentro del render de la página, y un hook en ella rompería
 * las reglas de hooks apenas la lista cambie de largo.
 */
function columnasDeDocumento({
  locale,
  money,
  canCreate,
  canEdit,
  canVoid,
  pdfLoading,
  onEdit,
  onEmit,
  onCancel,
  onVoid,
  onDelete,
  onAdjust,
  onPdf,
  onSendEmail,
  onPaymentLink,
  onClone,
  onUseAsTemplate,
  onConvertToSubscription,
  onRetry,
}: {
  locale: string
  money: (v: string | number) => string
  canCreate: boolean
  canEdit: boolean
  /** Solo los países cuyo régimen contempla la baja ante la autoridad (Perú). */
  canVoid: boolean
  pdfLoading: Set<string>
  onEdit: (d: SaleDocumentListItem) => void
  onEmit: (d: SaleDocumentListItem) => void
  onCancel: (d: SaleDocumentListItem) => void
  onVoid: (d: SaleDocumentListItem) => void
  onDelete: (d: SaleDocumentListItem) => void
  onAdjust: (d: SaleDocumentListItem) => void
  onPdf: (d: SaleDocumentListItem) => void
  onSendEmail: (d: SaleDocumentListItem) => void
  onPaymentLink: (d: SaleDocumentListItem) => void
  onClone: (d: SaleDocumentListItem) => void
  onUseAsTemplate: (d: SaleDocumentListItem) => void
  onConvertToSubscription: (d: SaleDocumentListItem) => void
  onRetry: (d: SaleDocumentListItem) => void
}): DataTableColumn<SaleDocumentListItem>[] {
  return [
    // 🔴 Las `key` ordenables son los nombres que el backend espera en
    // `?ordering=`: este listado lo pagina el SERVIDOR.
    { key: 'folio', header: 'Folio', sortable: true,
      render: doc => (
        <p className="text-sm font-mono font-semibold text-content whitespace-nowrap">
          {doc.folio ? `#${doc.folio}` : '—'}
        </p>
      ) },
    { key: 'document_type', header: 'Tipo',
      render: doc => <DocTypeBadge type={doc.document_type} /> },
    { key: 'issue_date', header: 'Fecha', sortable: true,
      render: doc => (
        <p className="text-sm text-content whitespace-nowrap">{fmtDate(doc.issue_date, locale)}</p>
      ) },
    { key: 'customer_name', header: 'Cliente',
      render: doc => (
        <div className="max-w-[14rem]">
          <p className="text-sm font-medium text-content truncate" title={doc.customer_name}>
            {doc.customer_name}
          </p>
          {doc.customer_tax_id && (
            <p className="text-xs text-content-subtle font-mono">{formatRut(doc.customer_tax_id)}</p>
          )}
        </div>
      ) },
    { key: 'sales_order_number', header: 'Pedido',
      render: doc => (doc.sales_order_number
        ? <span className="text-xs font-mono text-primary">{doc.sales_order_number}</span>
        : <span className="text-xs text-content-subtle">—</span>) },
    { key: 'status', header: 'Estado',
      render: doc => (
        <div className="flex flex-col gap-1">
          <StatusBadge status={doc.status} />
          {doc.status === 'accepted' && doc.accounting_status === 'pending' && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-warning-surface text-warning-content">
              <span className="w-1.5 h-1.5 rounded-full bg-warning-solid" />
              Sin contabilizar
            </span>
          )}
          <AdjustmentBadge status={doc.adjustment_status} docType={doc.document_type} />
        </div>
      ) },
    { key: 'collection_status', header: 'Cobranza',
      render: doc => (
        <CollectionBadge
          status={doc.collection_status}
          display={doc.collection_status_display}
          adjustmentStatus={doc.adjustment_status}
        />
      ) },
    { key: 'total', header: 'Total', numeric: true, sortable: true,
      render: doc => <p className="text-sm font-semibold text-content">{money(doc.total)}</p> },
    { key: 'acciones', header: '', width: 260,
      render: doc => (
        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
          {canEdit && doc.status === 'draft' && (
            <>
              <button
                onClick={() => onEmit(doc)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary-hover border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                title="Emitir"
              >
                <Send size={11} />Emitir
              </button>
              <button
                onClick={() => onEdit(doc)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
                title="Editar"
              >
                <FileText size={11} />Editar
              </button>
            </>
          )}
          {doc.status === 'draft' && (
            <button
              onClick={() => onPdf(doc)}
              disabled={pdfLoading.has(doc.id)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              title="Vista previa del documento (borrador, sin validez tributaria)"
            >
              {pdfLoading.has(doc.id) ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}PDF
            </button>
          )}
          {doc.status === 'accepted' && (
            <button
              onClick={() => onPdf(doc)}
              disabled={pdfLoading.has(doc.id)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-success-content border border-success-border rounded-lg hover:bg-success-surface transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              title="Descargar PDF"
            >
              {pdfLoading.has(doc.id) ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}PDF
            </button>
          )}
          {canCreate && doc.status === 'accepted' && (
            <button
              onClick={() => onAdjust(doc)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-colors"
              title="Nota de ajuste"
            >
              <FileMinus2 size={11} />Ajuste
            </button>
          )}
          {canEdit && doc.status === 'rejected' && (
            <button
              onClick={() => onEdit(doc)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
              title="Editar y corregir (vuelve a borrador)"
            >
              <FileText size={11} />Editar
            </button>
          )}
          {canEdit && (doc.status === 'pending_send' || doc.status === 'rejected') && (
            <button
              onClick={() => onRetry(doc)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-warning-content border border-warning-border rounded-lg hover:bg-warning-surface transition-colors"
              title="Reintentar envío"
            >
              <RefreshCw size={11} />Reintentar
            </button>
          )}
          <KebabMenu items={[
            { label: 'Enviar por correo', icon: <Mail size={14} />, onClick: () => onSendEmail(doc), hidden: doc.status !== 'accepted', className: 'text-primary dark:text-primary-hover hover:bg-primary-50' },
            { label: 'Link de pago', icon: <Link size={14} />, onClick: () => onPaymentLink(doc), hidden: doc.status !== 'accepted', className: 'text-primary dark:text-primary-hover hover:bg-primary-50' },
            { label: 'Clonar', icon: <Files size={14} />, onClick: () => onClone(doc), hidden: !canCreate },
            { label: 'Usar como plantilla', icon: <Copy size={14} />, onClick: () => onUseAsTemplate(doc), hidden: !canCreate || !['accepted','draft','rejected','cancelled'].includes(doc.status) },
            { label: 'Crear suscripción', icon: <RefreshCw size={14} />, onClick: () => onConvertToSubscription(doc), hidden: !canCreate || !['accepted','draft'].includes(doc.status), className: 'text-info-content-soft hover:bg-info-surface' },
            { label: 'Eliminar', icon: <Trash2 size={14} />, onClick: () => onDelete(doc), hidden: !canEdit || doc.status !== 'draft', className: 'text-danger-content-soft hover:bg-danger-surface' },
            { label: 'Anular', icon: <XCircle size={14} />, onClick: () => onCancel(doc), hidden: !canEdit || doc.status !== 'rejected', className: 'text-danger-content-soft hover:bg-danger-surface' },
            // Baja ante SUNAT: NO es "Anular". Esa es interna y solo aplica a
            // rechazados; esta opera sobre un comprobante ACEPTADO, tiene plazo y
            // puede ser rechazada. Solo se ofrece donde el régimen la contempla.
            { label: doc.document_type === 'BE' ? 'Anular boleta ante SUNAT' : 'Comunicar baja a SUNAT', icon: <FileMinus2 size={14} />, onClick: () => onVoid(doc), hidden: !canVoid || doc.status !== 'accepted', className: 'text-danger-content-soft hover:bg-danger-surface' },
          ]} />
        </div>
      ) },
  ]
}

// ─── SaleDocumentsPage ────────────────────────────────────────────────────────

export function SaleDocumentsPage() {
  const locale = useTenantLocale()
  // ⚠️ Resuelve el locale y la moneda del tenant: con `Intl` fijo en es-CL/CLP
  // un total peruano salía «$590» en vez de «S/ 590.00». Se declara acá y viaja
  // a las columnas, que no pueden usar hooks.
  const money = useMoney()
  const navigate = useNavigate()
  const { can } = usePermission()
  const canCreate = can('sales.documents', 'create')
  const canEdit = can('sales.documents', 'edit')
  const [searchParams, setSearchParams] = useSearchParams()
  // Estado de vista persistido en sessionStorage: al entrar a la Vista 360 (que
  // "contrae" el listado al rail) y volver, se restaura la vista previa
  // (página, tamaño y filtros de personalizados). Los filtros estándar se
  // restauran vía useFilters({ persistKey }).
  const persistedView = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('sales-docs:view')
      return raw ? (JSON.parse(raw) as { page?: number; pageSize?: number; customFieldValues?: Record<string, string> }) : null
    } catch { return null }
  }, [])
  // Filtros de campos personalizados: mapa nombre→valor (un control por campo
  // marcado como filtrable). Se serializa a 'A:x|B:y' para el backend.
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>(() => persistedView?.customFieldValues ?? {})
  const [page, setPage] = useState(() => persistedView?.page ?? 1)
  const [pageSize, setPageSize] = useState(() => persistedView?.pageSize ?? DEFAULT_PAGE_SIZE)
  // Filtro por cliente vía deep-link (?customer_id=&customer_name=), p. ej. desde
  // el botón "Ver todos los documentos del cliente" de la Vista 360.
  const [customerFilter, setCustomerFilter] = useState<{ id: string; name: string } | null>(null)
  const { data: dteTypes = [] } = useDTETypes()

  const typeOptions: FilterOption[] = useMemo(() => [
    { value: '', label: 'Todos los tipos' },
    ...dteTypes.filter(t => t.is_active)
      .sort((a, b) => a.display_order - b.display_order)
      .map(t => ({ value: t.internal_code, label: t.display_name })),
  ], [dteTypes])

  const { data: sellersData } = useSellers()
  const sellerOptions = useMemo(
    () => [{ value: '', label: 'Todos' }, ...(sellersData?.sellers ?? []).map(s => ({ value: s.id, label: s.full_name || s.email }))],
    [sellersData],
  )

  // Controles estándar del sistema (select/segment/date-range), dispuestos como
  // el mockup vía DocumentsFilterToolbar. Estado/Cobranza vuelven a 'select'
  // (control original) por consistencia con el resto de Clobi.
  const filterConfig: FilterConfig = useMemo(() => [
    { key: 'search', type: 'search' as const, placement: 'quick' as const, grow: true,
      placeholder: 'Buscar por folio, RUT, razón social o monto (> 100000, < 50000, = 1000)...' },
    { key: 'type', type: 'select' as const, label: 'Tipo', placement: 'quick' as const,
      options: typeOptions, widthClass: 'w-48' },
    { key: 'seller_id', type: 'select' as const, label: 'Vendedor',
      placement: 'quick' as const, options: sellerOptions, widthClass: 'w-40' },
    { key: 'date', type: 'date-range' as const, label: 'Fecha emisión',
      placement: 'quick' as const, defaultPreset: 'current-month' as const, presets: true },
    { key: 'status', type: 'select' as const, label: 'Estado', placement: 'quick' as const,
      options: [
        { value: '', label: 'Todos' },
        { value: 'draft', label: 'Borrador' },
        { value: 'accepted', label: 'Aceptado' },
        { value: 'rejected', label: 'Rechazado' },
        { value: 'cancelled', label: 'Anulado' },
      ] },
    { key: 'collection_status', type: 'select' as const, label: 'Cobranza',
      placement: 'quick' as const,
      options: [
        { value: '', label: 'Todas' },
        { value: 'paid', label: 'Pagada' },
        { value: 'partial', label: 'Parcial' },
        { value: 'pending', label: 'Pendiente' },
        { value: 'overdue', label: 'Vencida' },
      ] },
  ], [typeOptions, sellerOptions])

  const filters = useFilters(filterConfig, { onFilterChange: () => setPage(1), persistKey: 'sales-docs' })
  const [sort, setSort] = useState<SortState>(() =>
    loadSortFromStorage('sales-docs-sort', { field: 'issue_date', direction: 'desc' }),
  )
  const handleSortChange = (next: SortState) => {
    setSort(next)
    saveSortToStorage('sales-docs-sort', next)
    setPage(1)
  }
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<SaleDocument> | null>(null)
  const [editDocId, setEditDocId] = useState<string | null>(null)
  const [cancelTarget, setCancelTarget] = useState<SaleDocumentListItem | null>(null)
  const [voidTarget, setVoidTarget] = useState<SaleDocumentListItem | null>(null)
  const [adjustTarget, setAdjustTarget] = useState<SaleDocumentListItem | null>(null)
  const [adjustDocId, setAdjustDocId] = useState<string | null>(null)
  const [detailDocId, setDetailDocId] = useState<string | null>(null)
  const [paymentLinkDocId, setPaymentLinkDocId] = useState<string | null>(null)
  const [sendEmailDoc, setSendEmailDoc] = useState<SaleDocumentListItem | null>(null)
  const [templateDocId, setTemplateDocId] = useState<string | null>(null)
  const [subscriptionFromDoc, setSubscriptionFromDoc] = useState<{
    customer: string; document_type: string; lines: any[]; notes: string
  } | null>(null)
  const templateTriggered = useRef(false)

  const { data: locConfig } = useLocalizationConfig()
  // La comunicación de baja solo se ofrece donde el régimen la contempla y a
  // quien puede dejar un documento sin efecto (mismo permiso que anular). El
  // backend valida igual: esto decide si el ítem del menú aparece, no si la
  // baja procede — eso lo responde `GET /void/` al abrir el modal.
  const puedeComunicarBaja =
    can('sales.documents', 'approve') && paisUsaComunicacionDeBaja(locConfig?.country_code)

  // Persistir la vista (página/tamaño/personalizados) para restaurarla al volver
  // desde la Vista 360.
  useEffect(() => {
    try {
      sessionStorage.setItem('sales-docs:view', JSON.stringify({ page, pageSize, customFieldValues }))
    } catch { /* ignore */ }
  }, [page, pageSize, customFieldValues])

  // Auto-open detail desde ?highlight= (navegación interna) o ?open= (deep-link
  // de notificación/URL compartible, #77). Fetch-by-id directo, así que abre el
  // documento aunque NO esté en la página/filtros actuales. Con la Vista 360
  // habilitada, redirige al workspace 360 (el detalle ES el 360); si no, modal.
  useEffect(() => {
    // ?adjust=<id> (p. ej. desde la Vista 360) dispara el flujo de Nota de
    // ajuste sobre ese documento, sin importar el flag 360.
    const adjustId = searchParams.get('adjust')
    if (adjustId) {
      setAdjustDocId(adjustId)
      setSearchParams({}, { replace: true })
      return
    }
    const target = searchParams.get('highlight') || searchParams.get('open')
    if (!target) return
    // El detalle ES la Vista 360 (la lista colapsa al rail de la izquierda).
    navigate(`/sales/documents/${target}/360`, { replace: true })
  }, [searchParams, setSearchParams, navigate])

  // Deep-link de filtro por cliente: lo leemos una vez, lo aplicamos como chip y
  // limpiamos la URL (igual que ?open=). El nombre viene en la URL para el chip.
  useEffect(() => {
    const cid = searchParams.get('customer_id')
    if (!cid) return
    setCustomerFilter({ id: cid, name: searchParams.get('customer_name') || 'Cliente seleccionado' })
    setPage(1)
    const next = new URLSearchParams(searchParams)
    next.delete('customer_id')
    next.delete('customer_name')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const personalizadosParam = useMemo(() =>
    Object.entries(customFieldValues)
      .filter(([, v]) => v && v.trim())
      .map(([k, v]) => `${k}:${v.trim()}`)
      .join('|') || undefined,
  [customFieldValues])

  // Filtros efectivos del listado (sin paginación). Se reutilizan para el fetch
  // y se persisten para que el rail de la Vista 360 muestre EXACTAMENTE el mismo
  // conjunto filtrado/ordenado que el usuario venía viendo.
  // Filtro por contrato (deep-link desde la página de Contratos → "ver documentos").
  const contractFilter = searchParams.get('contract') || undefined

  const listParams = useMemo(() => ({
    ...filters.params,
    personalizados: personalizadosParam,
    customer_id: customerFilter?.id,
    contract: contractFilter,
    ordering: sortStateToParam(sort),
  }), [filters.params, personalizadosParam, customerFilter?.id, contractFilter, sort])

  // Persistir los filtros efectivos para el rail de la Vista 360: así arranca con
  // el MISMO conjunto (búsqueda/cliente/tipo/estado/orden) en vez de traer todos
  // los documentos recientes. Se limpian las claves vacías.
  useEffect(() => {
    try {
      const clean = Object.fromEntries(
        Object.entries(listParams).filter(([, v]) => v != null && v !== ''),
      )
      sessionStorage.setItem('sales-docs:rail-params', JSON.stringify(clean))
    } catch { /* ignore */ }
  }, [listParams])

  const { data, isLoading } = useSaleDocuments({ ...listParams, page, page_size: pageSize })
  const { data: customersData } = useCustomers({ active: 'true', page_size: 9999 })
  const { data: globalFields = [] } = useGlobalCustomFields(true)
  // Campos personalizados de ventas marcados como filtrables → controles individuales.
  const filterableFields = useMemo(
    () => globalFields.filter(f => f.usable_in_filters && f.scope === 'ventas' && f.is_active),
    [globalFields],
  )
  const setCustomField = (name: string, value: string) => {
    setCustomFieldValues(prev => ({ ...prev, [name]: value }))
    setPage(1)
  }
  // "Más filtros" vive en el header (junto a Plantillas/Nuevo doc), no en el flujo
  // de filtros. Estado anclable: se persiste para que quien siempre los usa los
  // deje fijos. hasAdvanced = hay defs avanzados en el config o campos filtrables.
  const hasAdvancedFilters = filterConfig.some(f => f.placement === 'advanced') || filterableFields.length > 0
  const [advancedOpen, setAdvancedOpen] = useState(() => {
    try { return localStorage.getItem('sales-docs:filters-advanced-open') === '1' } catch { return false }
  })
  const toggleAdvanced = () => setAdvancedOpen(prev => {
    const next = !prev
    try { localStorage.setItem('sales-docs:filters-advanced-open', next ? '1' : '0') } catch { /* ignore */ }
    return next
  })
  const { data: currencyRatesData } = useCurrencyRates()
  const { data: detailDoc, isLoading: isLoadingDetail, error: detailError } = useSaleDocument(detailDocId)
  const { data: editDoc } = useSaleDocument(editDocId)
  const { data: templateDoc } = useSaleDocument(templateDocId)
  const { data: adjustDoc } = useSaleDocument(adjustDocId)

  // When a full edit document loads, open the modal with all data
  useEffect(() => {
    if (editDoc && editDocId) {
      setEditing(editDoc)
      setModalOpen(true)
      setEditDocId(null)
    }
  }, [editDoc, editDocId])

  const { confirm: confirmAction, modalProps: confirmProps } = useConfirmModal()

  // When adjust source doc loads, pre-fill NC with all original data + referencias.
  // Si la FE ya fue pagada (outstanding <= 0), mostramos warning antes de abrir el form.
  useEffect(() => {
    if (!adjustDoc || !adjustDocId) return

    const openAdjustForm = () => {
      const TIPO_DOC: Record<string, string> = {
        FE: '33', FEE: '34', BE: '39', BEE: '41',
        FC: '46', ND: '56', NC: '61',
        FX: '110', FXE: '111', FXA: '112',
      }
      const tipoDoc = TIPO_DOC[adjustDoc.document_type] ?? '33'
      const folioRef = String((adjustDoc.localization_data as any)?.folio ?? '')
      const referencia = {
        tipo_doc: tipoDoc,
        folio_ref: folioRef,
        fecha_ref: adjustDoc.issue_date,
        cod_ref: '1',
        razon_ref: `Anula ${adjustDoc.document_type} ${folioRef}`.trim(),
      }
      setAdjustTarget({
        id: adjustDoc.id,
        exchange_rate_data: adjustDoc.exchange_rate_data,
      } as unknown as SaleDocumentListItem)
      // Heredar referencias del documento original (ej. OC con tipo_doc=801)
      // y anteponer el "Anula" auto-generado. El usuario puede editar/quitar
      // cualquiera de los dos antes de emitir.
      const originalRefs = (adjustDoc.referencias || []).filter(
        // Filtra refs vacías que el viejo seed deja a veces.
        (r: any) => r && (r.folio_ref || r.razon_ref),
      )
      setEditing({
        customer: adjustDoc.customer,
        document_type: 'NC',
        reference_document: adjustDoc.id,
        payment_method: adjustDoc.payment_method,
        notes: adjustDoc.notes,
        lines: adjustDoc.lines,
        referencias: [referencia, ...originalRefs],
        personalizados: adjustDoc.personalizados,
        // Pasa la tasa bloqueada del original para que el preview de totales
        // no se recotice a la tasa vigente del día.
        exchange_rate_data: adjustDoc.exchange_rate_data,
      } as any)
      setModalOpen(true)
    }

    const outstanding = parseFloat(adjustDoc.outstanding ?? '0')
    const total = parseFloat(adjustDoc.total ?? '0')
    setAdjustDocId(null)

    if (outstanding <= 0 && total > 0) {
      const paidAmount = total - outstanding
      const fmtMonto = (v: number) => formatMoneyForTenant(v)
      confirmAction({
        message:
          `Esta factura ya fue pagada (${fmtMonto(paidAmount)}) y no tiene saldo pendiente. ` +
          `Si emites esta Nota de Crédito, se generará un crédito de ${fmtMonto(total)} a favor del cliente. ` +
          `Las políticas de reembolso se habilitarán en una versión futura. ¿Deseas continuar?`,
        variant: 'warning',
        confirmLabel: 'Continuar',
        onConfirm: openAdjustForm,
      })
    } else {
      openAdjustForm()
    }
  }, [adjustDoc, adjustDocId, confirmAction])

  // When a template document loads, pre-fill the modal
  useEffect(() => {
    if (!templateDoc || !templateDocId || templateTriggered.current) return
    templateTriggered.current = true
    const today = todayISO()
    setEditing({
      customer: templateDoc.customer,
      document_type: templateDoc.document_type,
      lines: templateDoc.lines,
      notes: templateDoc.notes,
      issue_date: today,
      due_date: null,
      referencias: [],
      personalizados: templateDoc.personalizados ?? [],
    })
    setModalOpen(true)
    setTemplateDocId(null)
    templateTriggered.current = false
  }, [templateDoc, templateDocId])

  const { mutate: create, isPending: isCreating } = useCreateDocument()
  const { mutate: emit, isPending: isEmitting } = useEmitDocument()
  const { mutate: cancel, isPending: isCancelling } = useCancelDocument()
  const deleteDraft = useDeleteDraftDocument()
  const { mutate: adjust, isPending: isAdjusting } = useAdjustDocument()
  const retryMutation = useRetryDocumentSend()
  const toast = useToast()
  // Surface del error del backend (antes create/update/adjust del modal no tenían
  // onError → un 400 como "El precio unitario debe ser mayor que 0" se tragaba en
  // silencio y el usuario no veía por qué no se guardaba la NC/documento).
  const showError = (e: unknown, fallback: string) => {
    const details = (e as { response?: { data?: { error?: { details?: unknown } } } })
      ?.response?.data?.error?.details
    toast.error(firstErrorString(details) || extractErrorMessage(e, fallback))
  }
  const updateMutation = useUpdateDocument(editing?.id || '')
  const { mutate: saveAsTemplate } = useSaveDocumentAsTemplate()
  const { mutate: cloneDoc } = useCloneDocument()

  const customers = customersData?.customers || []
  const documents = data?.documents || []
  const total = data?.count || 0
  const pages = data?.pages || 1

  const [pdfLoading, setPdfLoading] = useState<Set<string>>(new Set())
  const handlePdf = async (doc: SaleDocumentListItem) => {
    if (pdfLoading.has(doc.id)) return
    setPdfLoading(prev => new Set(prev).add(doc.id))
    try {
      const res = await apiClient.get(`/sales/documents/${doc.id}/pdf/`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      window.open(url, '_blank')
    } catch {
      // silently fail — user can retry
    } finally {
      setPdfLoading(prev => {
        const next = new Set(prev)
        next.delete(doc.id)
        return next
      })
    }
  }

  const handleUseAsTemplate = (doc: SaleDocumentListItem) => {
    const name = window.prompt('Nombre de la plantilla:', `Plantilla ${doc.document_type_display}`)
    if (!name) return
    saveAsTemplate({ documentId: doc.id, name })
  }

  const handleClone = (doc: SaleDocumentListItem) => {
    cloneDoc(doc.id, {
      onSuccess: (newDoc) => navigate(`/sales/documents/${newDoc.id}/edit`),
    })
  }

  const handleConvertToSubscription = async (doc: SaleDocumentListItem) => {
    const res = await apiClient.get(`/sales/documents/${doc.id}/`)
    const fullDoc: SaleDocument = res.data
    setSubscriptionFromDoc({
      customer: fullDoc.customer,
      document_type: fullDoc.document_type,
      lines: fullDoc.lines.map(l => ({
        sku: l.sku,
        name: l.name,
        unit: l.unit,
        quantity: l.quantity,
        unit_price: l.unit_price,
      })),
      notes: fullDoc.notes,
    })
  }

  const handleCreate = () => {
    navigate('/sales/documents/new')
  }

  const handleEdit = (doc: SaleDocumentListItem) => {
    navigate(`/sales/documents/${doc.id}/edit`)
  }

  const handleView = (doc: SaleDocumentListItem) => {
    // El detalle ES la Vista 360 (la lista colapsa al rail de la izquierda).
    navigate(`/sales/documents/${doc.id}/360`)
  }

  const cleanDocData = (formData: any) => {
    const { due_date, ...rest } = formData  // due_date is auto-calculated in backend
    return rest
  }

  const handleModalSubmit = (formData: any) => {
    const data = cleanDocData(formData)
    if (editing?.id) {
      updateMutation.mutate(data, {
        onSuccess: () => setModalOpen(false),
        onError: (e) => showError(e, 'No se pudo guardar el documento.'),
      })
    } else {
      create(data, {
        onSuccess: () => setModalOpen(false),
        onError: (e) => showError(e, 'No se pudo crear el documento.'),
      })
    }
  }

  const handleEmit = (doc: SaleDocumentListItem) => {
    // Guard de doble emisión (#369): ignorar clics repetidos mientras una
    // emisión está en vuelo. El backend ya bloquea con 409, esto evita el
    // round-trip y el parpadeo de error.
    if (isEmitting) return
    // Sin onError la pantalla callaba el rechazo (falta de CAF, validación del
    // receptor): el documento seguía en borrador y solo la consola veía el 400.
    emit(doc.id, {
      onError: (e) => showError(e, 'No se pudo emitir el documento.'),
    })
  }

  const handleCancelConfirm = (reason: string) => {
    if (!cancelTarget) return
    cancel(
      { id: cancelTarget.id, reason },
      { onSuccess: () => setCancelTarget(null) }
    )
  }

  const handleAdjust = (doc: SaleDocumentListItem) => {
    setAdjustDocId(doc.id)
  }

  // Eliminar DEFINITIVAMENTE un borrador (solo draft; rechazados se anulan).
  const handleDeleteDraft = (doc: SaleDocumentListItem) => {
    confirmAction({
      message:
        `¿Eliminar definitivamente el borrador ${doc.document_type_display} de ${doc.customer_name}? ` +
        `Esta acción no se puede deshacer.`,
      variant: 'danger',
      confirmLabel: 'Eliminar',
      onConfirm: () => new Promise<void>((resolve) => {
        deleteDraft.mutate(doc.id, { onSettled: () => resolve() })
      }),
    })
  }

  const handleAdjustSubmit = (formData: any) => {
    if (!adjustTarget) return
    adjust(
      { id: adjustTarget.id, data: cleanDocData(formData) },
      {
        onSuccess: () => {
          setModalOpen(false)
          setAdjustTarget(null)
        },
        onError: (e) => showError(e, 'No se pudo crear la nota de ajuste.'),
      }
    )
  }

  return (
    <div data-tour="sales-docs-page" className="p-4 sm:p-6 max-w-7xl mx-auto">
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
      {/* Header */}
      <PageHeader
        title="Documentos de Venta"
        subtitle={`${total} ${total === 1 ? 'documento' : 'documentos'} registrados`}
        help={HELP['sales.documents']}
      >
        {hasAdvancedFilters && (
          <button
            onClick={toggleAdvanced}
            title={advancedOpen ? 'Ocultar filtros adicionales' : 'Mostrar y anclar filtros adicionales'}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              advancedOpen
                ? 'border-primary text-primary bg-primary/5'
                : 'text-content-muted border-divider hover:bg-soft'
            }`}
          >
            <SlidersHorizontal size={16} />Más filtros
            {filters.advancedFilterCount > 0 && (
              <span className="px-1.5 py-0.5 bg-primary text-on-primary text-[10px] rounded-full leading-none">
                {filters.advancedFilterCount}
              </span>
            )}
          </button>
        )}
        {canCreate && (
          <button
            onClick={() => navigate('/sales/templates')}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
          >
            <Copy size={16} />Plantillas
          </button>
        )}
        {canCreate && (
          <button
            data-tour="sales-docs-new"
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors"
          >
            <Plus size={16} />Nuevo documento
          </button>
        )}
      </PageHeader>

      {/* Filtros — controles estándar dispuestos como el mockup; avanzados inline */}
      <div data-tour="sales-docs-filters">
        <DocumentsFilterToolbar
          config={filterConfig}
          filters={filters}
          advancedOpen={advancedOpen}
          advancedChildren={filterableFields.length > 0 ? (
            <CustomFieldFilters
              fields={filterableFields}
              values={customFieldValues}
              onChange={setCustomField}
            />
          ) : undefined}
        />
      </div>

      {/* Chip de filtro por cliente (deep-link desde la Vista 360) */}
      {customerFilter && (
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
            <UserSquare2 size={13} />
            Cliente: {customerFilter.name}
            <button
              type="button"
              onClick={() => { setCustomerFilter(null); setPage(1) }}
              className="ml-0.5 p-0.5 rounded-full hover:bg-primary/20"
              aria-label="Quitar filtro de cliente"
            >
              <X size={12} />
            </button>
          </span>
        </div>
      )}

      {/* Chip de filtro por contrato (deep-link desde la página de Contratos) */}
      {contractFilter && (
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
            <FileText size={13} />
            Contrato: {documents[0]?.contract_number || '—'}
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams)
                next.delete('contract')
                setSearchParams(next, { replace: true })
                setPage(1)
              }}
              className="ml-0.5 p-0.5 rounded-full hover:bg-primary/20"
              aria-label="Quitar filtro de contrato"
            >
              <X size={12} />
            </button>
          </span>
        </div>
      )}

      {/* Tabla */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <TableSkeleton rows={8} cols={7} />
        ) : documents.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No hay documentos"
            hint={filters.hasActiveFilters
              ? 'Prueba con otros filtros o términos de búsqueda.'
              : 'Crea el primer documento con el botón de arriba.'}
          />
        ) : (
          <DataTable<SaleDocumentListItem>
            columns={columnasDeDocumento({
              locale, money, canCreate, canEdit, canVoid: puedeComunicarBaja,
              pdfLoading,
              onEdit: handleEdit,
              onEmit: handleEmit,
              onCancel: d => setCancelTarget(d),
              onVoid: d => setVoidTarget(d),
              onDelete: handleDeleteDraft,
              onAdjust: handleAdjust,
              onPdf: handlePdf,
              onSendEmail: d => setSendEmailDoc(d),
              onPaymentLink: d => setPaymentLinkDocId(d.id),
              onClone: handleClone,
              onUseAsTemplate: handleUseAsTemplate,
              onConvertToSubscription: handleConvertToSubscription,
              onRetry: d => retryMutation.mutate(d.id),
            })}
            rows={documents}
            rowKey={d => d.id}
            minWidth={1180}
            surface={false}
            data-tour="sales-docs-table"
            onRowClick={handleView}
            {...ordenControlado(sort, handleSortChange)}
            caption="Documentos de venta emitidos y en borrador"
          />
        )}
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between mt-4 gap-4 flex-wrap">
        <PageSizeSelector
          value={pageSize}
          onChange={n => { setPageSize(n); setPage(1) }}
          storageKey="sale-documents-page-size"
        />
        {/* Sin `onPageSizeChange`: el tamaño ya lo maneja el `PageSizeSelector`
            de arriba, y ofrecerlo dos veces deja dos controles para lo mismo. */}
        {pages > 1 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            locale={locale}
          />
        )}
      </div>

      {/* Modal crear/editar */}
      <DocumentModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setAdjustTarget(null) }}
        onSubmit={adjustTarget ? handleAdjustSubmit : handleModalSubmit}
        isPending={editing?.id && !adjustTarget ? updateMutation.isPending : adjustTarget ? isAdjusting : isCreating}
        initial={editing}
        customers={customers}
        globalFields={globalFields}
        currencyRates={currencyRatesData?.rates}
      />

      {/* Modal anulación */}
      {voidTarget && (
        <VoidModal doc={voidTarget} onClose={() => setVoidTarget(null)} />
      )}

      {cancelTarget && (
        <CancelModal
          docName={`${cancelTarget.document_type_display} — ${cancelTarget.customer_name}`}
          onConfirm={handleCancelConfirm}
          onCancel={() => setCancelTarget(null)}
          isPending={isCancelling}
        />
      )}

      {/* Modal detalle */}
      <DocumentDetailModal
        open={!!detailDocId}
        onClose={() => setDetailDocId(null)}
        doc={detailDoc || null}
        customers={customers}
        loading={isLoadingDetail}
        error={detailError}
      />

      {/* Modal link de pago */}
      {paymentLinkDocId && (
        <PaymentLinkModal
          docId={paymentLinkDocId}
          onClose={() => setPaymentLinkDocId(null)}
        />
      )}

      {/* Modal convertir a suscripción */}
      {subscriptionFromDoc && (
        <SubscriptionModal
          initialDoc={subscriptionFromDoc}
          onClose={() => setSubscriptionFromDoc(null)}
        />
      )}

      {/* Modal enviar por correo */}
      {sendEmailDoc && (
        <SendEmailModal
          open={!!sendEmailDoc}
          onClose={() => setSendEmailDoc(null)}
          documentId={sendEmailDoc.id}
          documentLabel={`${sendEmailDoc.document_type_display} N° ${sendEmailDoc.folio || sendEmailDoc.id.slice(0, 8)}`}
        />
      )}

      {/* Warning de ajuste sobre FE pagada */}
      <ConfirmModal {...confirmProps} />
    </div>
  )
}

// ─── Filtros de campos personalizados (controles individuales, Vista 360) ──────
// Cada campo marcado como "filtrable" en /config/fields se renderiza como su
// propio control (con su nombre real), no en un dropdown único.

function CustomFieldFilters({ fields, values, onChange }: {
  fields: GlobalCustomField[]
  values: Record<string, string>
  onChange: (name: string, value: string) => void
}) {
  const inputCls = 'px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200'
  return (
    <>
      {fields.map(f => {
        const val = values[f.nombre] ?? ''
        return (
          <div key={f.id} className="flex flex-col gap-1">
            <label className="text-xs font-medium text-content-subtle">{f.nombre}</label>
            {f.tipo === 'select' ? (
              <select aria-label={f.nombre} value={val} onChange={e => onChange(f.nombre, e.target.value)} className={inputCls}>
                <option value="">Todas</option>
                {(f.opciones || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.tipo === 'boolean' ? (
              <select aria-label={f.nombre} value={val} onChange={e => onChange(f.nombre, e.target.value)} className={inputCls}>
                <option value="">Todos</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            ) : f.tipo === 'fecha' ? (
              <DatePicker aria-label={f.nombre} value={val} onChange={v => onChange(f.nombre, v)} placeholder="Fecha" className={`w-[150px] ${inputCls}`} />
            ) : (
              <input aria-label={f.nombre}
                type={f.tipo === 'numero' ? 'number' : 'text'}
                value={val}
                onChange={e => onChange(f.nombre, e.target.value)}
                placeholder={`Filtrar por ${f.nombre.toLowerCase()}…`}
                className={`w-[180px] ${inputCls}`}
              />
            )}
          </div>
        )
      })}
    </>
  )
}
